import { Map as olMap } from 'ol'
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { WaypointsEntity } from "./WaypointEntity";
import WaypointFeature from "./WaypointFeature";
import { TrackEntity } from "./TrackEntity";
import TrackLineFeature from "./TrackLineFeature";

// Animates the journey of a "traveller" icon along a track or route

export default class Journey {

    static inProgress = null as Journey|null

    waypoints: WaypointsEntity
    path: LineString|null
    traveller= null as WaypointFeature|null

    runTimer: NodeJS.Timeout|undefined = undefined
    endTimer: NodeJS.Timeout|undefined = undefined
    
    distance = 0
    distancePerTick = 0
    msPerFrame = 0

    constructor(track: TrackEntity, wpts: WaypointsEntity) {
        this.waypoints = wpts  
        this.path = this.combineAllSegmentsIntoOnePath (track.getSegments())
        if (this.path) {
            this.traveller = this.addTravellingWaypoint (this.path, this.waypoints)
            this.calculateTheSpeed (this.path, track.renderer.map)  
        }
    } 

    toggle() {
        if (Journey.inProgress) 
            Journey.inProgress.stop();
        else if (this.path) {
            this.distance = 0
            Journey.inProgress = this
            this.runTimer = setInterval(() => this.advance(), this.msPerFrame)    
        }
    }
    
    combineAllSegmentsIntoOnePath(segs: TrackLineFeature[]): LineString|null {
        if (!segs[0]) return null // do nothing if there is no track [yet]
        const path = segs[0].getGeometry().clone(); 
        for (let i = 1; i < segs.length; i++) { 
            segs[i].getGeometry().getCoordinates().forEach(coord => path.appendCoordinate(coord))    
        }
        return path
    }

    addTravellingWaypoint (path: LineString, wpts: WaypointsEntity): WaypointFeature|null {
        const startPosition = path.getFirstCoordinate()
        const newIndex = wpts.dotsFeatureSource.getFeatures().length
        const traveller = new WaypointFeature(startPosition, wpts, newIndex)
        traveller.setIcon("mdi-crosshairs-gps")
        traveller.setColour("Indigo")
        this.waypoints.dotsFeatureSource.addFeature(traveller)
        return traveller
    }

    advance() { 
        if (this.distance > 1) {
            if (this.runTimer) clearInterval(this.runTimer)
            this.endTimer = setTimeout(() => this.stop(), 1000)
        } 
        else if (this.path && this.traveller) {
            const newPosition = new Point(this.path.getCoordinateAt(this.distance))
            this.traveller.setGeometry(newPosition)
            this.distance += this.distancePerTick
        }
    }

    stop() {
        Journey.inProgress = null
        if(this.runTimer) clearInterval(this.runTimer) // benign if already cancelled
        if(this.endTimer) clearTimeout(this.endTimer)
        const waypointToRemove = this.waypoints.dotsFeatureSource.getFeatureById(this.traveller!.getId()!)
        this.waypoints.dotsFeatureSource.removeFeature(waypointToRemove)
    }

    // This is way more sophisticated than it needs to be, but it's just a bit of fun! It sets a target speed in pixels
    // per sec after finding the fraction of the overall path length that is represented by each pixel (roughly)
    calculateTheSpeed(path: LineString, map: olMap) { 
        const pixelsPerSec = 100 
        const framesPerSec = 30
        this.msPerFrame = 1000 / framesPerSec 
        const pixelsPerFrame = pixelsPerSec / framesPerSec
        const p0 = map.getPixelFromCoordinate(path.getFirstCoordinate()) // at start
        const p1 = map.getPixelFromCoordinate(path.getCoordinateAt(0.05)) // at 5% distance along the path
        const lengthInPx = 20 * Math.sqrt(Math.pow(p0[0]-p1[0], 2) + Math.pow(p0[1]-p1[1], 2)) 
        this.distancePerTick = pixelsPerFrame / lengthInPx
    }

}